import { useState, useEffect, type FormEvent } from 'react'
import { useNavigate, useLocation, Link } from 'react-router-dom'
import { useAuth } from '../auth/AuthContext'
import './LoginPage.css'

// Microsoft logo SVG (official four-square mark)
function MicrosoftLogo() {
  return (
    <svg className="login-card__ms-icon" viewBox="0 0 21 21" xmlns="http://www.w3.org/2000/svg">
      <rect x="1"  y="1"  width="9" height="9" fill="#f25022"/>
      <rect x="11" y="1"  width="9" height="9" fill="#7fba00"/>
      <rect x="1"  y="11" width="9" height="9" fill="#00a4ef"/>
      <rect x="11" y="11" width="9" height="9" fill="#ffb900"/>
    </svg>
  )
}

export default function LoginPage() {
  const { loginLocal, loginMicrosoft, microsoftEnabled, isAuthenticated } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  // Operators land in the admin console; chats open only from employee magic links.
  const from = (location.state as { from?: { pathname: string } })?.from?.pathname ?? '/admin'

  // Redirect already-authenticated users to their intended destination
  if (isAuthenticated) { navigate(from, { replace: true }); return null; }

  const [email,    setEmail]    = useState('')
  const [password, setPassword] = useState('')
  const [error,    setError]    = useState<string | null>(null)
  const [loading,  setLoading]  = useState(false)
  const [notice,   setNotice]   = useState<string | null>(null)

  // Show any error the backend forwarded as a query param after the OAuth callback
  useEffect(() => {
    const params = new URLSearchParams(location.search)
    const backendError = params.get('error')
    if (params.get('reset') === '1') setNotice('Password updated. Sign in with your new password.')
    if (backendError === 'microsoft_disabled') setError('Microsoft sign-in is not enabled. Use your email and password.')
    else if (backendError) setError(decodeURIComponent(backendError))
  }, [location.search])

  async function handleSubmit(e: FormEvent) {
    e.preventDefault()
    setError(null)
    setLoading(true)
    try {
      await loginLocal(email, password)
      navigate(from, { replace: true })
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Login failed')
    } finally {
      setLoading(false)
    }
  }

  function handleMicrosoft() {
    // loginMicrosoft navigates the whole page to the backend OAuth entry point.
    // The backend performs the auth code exchange and redirects back.
    loginMicrosoft(from)
  }


  return (
    <div className="login-page">
      <div className="login-card">

        {/* Header */}
        <div className="login-card__header">
          <h1 className="login-card__title">Dräger AI Product Training</h1>
          <p className="login-card__subtitle">Sign in to your account</p>
        </div>

        {/* Error banner */}
        {error && <div className="login-card__error">{error}</div>}
        {notice && !error && <div className="login-card__notice" role="status">{notice}</div>}

        {/* Microsoft login — only when the backend has the Entra app configured */}
        {microsoftEnabled && (
          <>
            <button
              className="login-card__ms-btn"
              onClick={handleMicrosoft}
              disabled={loading}
              type="button"
            >
              <MicrosoftLogo />
              Sign in with Microsoft
            </button>

            {/* Divider */}
            <div className="login-card__divider">or continue with email</div>
          </>
        )}

        {/* Email + password form */}
        <form className="login-card__form" onSubmit={(e) => void handleSubmit(e)}>
          <div className="login-card__field">
            <label className="login-card__label" htmlFor="login-email">Email address</label>
            <input
              id="login-email"
              className="login-card__input"
              type="email"
              autoComplete="email"
              placeholder="you@company.com"
              value={email}
              onChange={e => setEmail(e.target.value)}
              required
              disabled={loading}
            />
          </div>

          <div className="login-card__field">
            <label className="login-card__label" htmlFor="login-password">Password</label>
            <input
              id="login-password"
              className="login-card__input"
              type="password"
              autoComplete="current-password"
              placeholder="••••••••"
              value={password}
              onChange={e => setPassword(e.target.value)}
              required
              disabled={loading}
            />
          </div>

          <button
            className="login-card__submit"
            type="submit"
            disabled={loading}
          >
            {loading && <span className="login-card__spinner" />}
            {loading ? 'Signing in…' : 'Sign in'}
          </button>
        </form>

        <div className="login-card__links">
          <Link to="/forgot-password" className="login-card__link">Forgot password?</Link>
          <span className="login-card__hint">Accounts are created by your administrator.</span>
        </div>

      </div>
    </div>
  )
}
